import type {OrgEditPermissions} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {Link} from '@github-ui/react-core/link'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {Box, CounterLabel, UnderlineNav} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {type ComponentProps, useState} from 'react'

import {type PropertiesPageTabName, useActiveTab} from '../hooks/use-active-tab'

interface Props {
  definitionsCount: number
  permissions: OrgEditPermissions
}

// Workaround the bug with invalid `activeClassName` prop.
const WrappedLink = ({activeClassName, ...props}: ComponentProps<typeof Link> & {activeClassName?: string}) => (
  <Link {...props} />
)

export function PropertiesPageTabs({definitionsCount, permissions}: Props) {
  const [activeTab, hrefBuilder] = useActiveTab(permissions)
  const [selectedTab, setSelectedTab] = useState<PropertiesPageTabName>(activeTab)

  const useUnderlineNav = useFeatureFlag('custom_properties_underline_nav')

  return useUnderlineNav ? (
    <UnderlineNav aria-label="Page selector" sx={{mb: 3}}>
      <UnderlineNav.Item
        key="properties-tab"
        as={Link}
        to={hrefBuilder('properties')}
        {...(selectedTab === 'properties' && {'aria-current': true})}
        onSelect={() => setSelectedTab('properties')}
        counter={human(definitionsCount)}
      >
        <span>Properties</span>
      </UnderlineNav.Item>
      <UnderlineNav.Item
        key="set-values-tab"
        as={Link}
        to={hrefBuilder('set-values')}
        {...(selectedTab === 'set-values' && {'aria-current': true})}
        onSelect={() => setSelectedTab('set-values')}
      >
        <span>Set values</span>
      </UnderlineNav.Item>
    </UnderlineNav>
  ) : (
    <TabNav aria-label="Page selector" sx={{mb: 3, svg: {color: 'fg.muted'}}}>
      <TabNav.Link as={WrappedLink} to={hrefBuilder('properties')} selected={activeTab === 'properties'}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
          <span>Properties</span>
          <CounterLabel>{human(definitionsCount)}</CounterLabel>
        </Box>
      </TabNav.Link>
      <TabNav.Link as={WrappedLink} to={hrefBuilder('set-values')} selected={activeTab === 'set-values'}>
        <span>Set values</span>
      </TabNav.Link>
    </TabNav>
  )
}

try{ WrappedLink.displayName ||= 'WrappedLink' } catch {}
try{ PropertiesPageTabs.displayName ||= 'PropertiesPageTabs' } catch {}